import { useCallback } from "react";
import dayjs from "dayjs";
// Store
import { useAppSelector } from "app/hooks/useStore";
// Selectors
import { selectPageEntities } from "app/store/page/page.selectors";
import { selectEpisodesEntities } from "app/store/Episodes/Episodes.selectors";

const usePagesUtilities = () => {
  const pageEntities = useAppSelector(selectPageEntities);
  const episodeEntities = useAppSelector(selectEpisodesEntities);

  const generateWorkspaceOrder = useCallback(({
    prevWorkspaceOrder,
    date,
    time,
    index
  }:{
    prevWorkspaceOrder?:string | undefined;
    date?:string | undefined;
    time?:string | undefined;
    index?:number | undefined;
  }) => {
    const mls = dayjs().millisecond();

    let orderDate:string = date ? date.replace(/-/gi, '') : '00000000';
    let orderTime:string = time ? time.replace(/:/gi, '') : '0000';

    let timeStamp:string = dayjs().millisecond(index ? mls + index : mls).utc().format('YYYYMMDDHHmmssSSS');

    if ( prevWorkspaceOrder ) timeStamp = prevWorkspaceOrder.substring(12, 29);
    if ( date ) orderDate = date.replace(/-/gi, '');
    if ( time ) orderTime = time.replace(/:/gi, '');

    return `${orderDate}${orderTime}${timeStamp}`;
  }, []);

  const getPagesByIds = useCallback((pageIds:string[], skipStapleCheck:boolean = false) => {
    const pages:any[] = [];
    const seenStapleIds = new Set<string>();
    for ( let pageId of pageIds ) {
      const [ documentId, pageNum ] = pageId.split(':').map(Number);
      // Can be `NaN` if the pageId is `undefined:undefined` from `Workspace`
      if ( !documentId || !pageNum ) continue;

      const page = pageEntities[documentId][pageNum];

      if ( !page ) continue;

      const hasStaple = page.staple;

      if ( hasStaple && !skipStapleCheck ){
        // We should not add the same staple page more than once
        if ( seenStapleIds.has(page.staple.id) ) continue;
        seenStapleIds.add(page.staple.id);
        const staplePages = Object.values(pageEntities[documentId])
          .filter((p: any) => p.staple && p.staple.id === page.staple.id);
        pages.push(...staplePages);
      } else {
        pages.push(page);
      }
    }
    return pages;
  }, [pageEntities]);

  const updatePagesInWorkspace = useCallback((pages:any[], inWorkspace:boolean = true) => {
    // We should exclude pages which already in the workspace or not in the workspace
    const excludedPages = pages.filter((page) => {
      const pageInWorkspace = page.inWorkspace || false;
      return pageInWorkspace !== inWorkspace;
    });

    const result = [];
    for ( let i = 0; i < excludedPages.length; i++ ){
      const page = excludedPages[i];
      // Default to the current page's episode
      let parentEpisodeId = page.parentEpisodeId;

      // If page is not the primary staple, find the primary staple page
      if ( page.staple && page.staple.order > 1 ){
        const primaryStaplePage = pages.find((p) => p.staple && p.staple.id === page.staple.id && p.staple.order === 1);
        if ( primaryStaplePage ){
          parentEpisodeId = primaryStaplePage.parentEpisodeId;
        }
      }

      if ( inWorkspace ){
        const pageEpisode = parentEpisodeId ? episodeEntities[parentEpisodeId] : null;
        result.push({
          documentId: page.documentId,
          pageNum: page.pageNum,
          version: page.version,
          inWorkspace: true,
          workspaceOrder: generateWorkspaceOrder({
            prevWorkspaceOrder: page.workspaceOrder,
            date: pageEpisode?.date,
            time: pageEpisode?.time,
            index: i
          })
        });
      } else {
        result.push({
          documentId: page.documentId,
          pageNum: page.pageNum,
          version: page.version,
          inWorkspace: false,
          workspaceOrder: null
        });
      }
    }

    return result;
  }, [episodeEntities, generateWorkspaceOrder]);

  // Assign pages colors
  const assignPagesColors = useCallback((pages:any[], colors:string[]) => {
    const result:any[] = [];

    for (const page of pages) {
      const current:string[] = page.colors || [];
      const newColors = Array.from(new Set([...current, ...colors]));

      if (newColors.length === current.length) continue;

      result.push({
        documentId: page.documentId,
        pageNum: page.pageNum,
        version: page.version,
        colors: newColors
      });
    }

    return result;
  }, []);

  // Unassign pages colors
  const unassignPagesColors = useCallback((pages:any[], colors:string[]) => {
    const result:any[] = [];

    for (const page of pages) {
      const current:string[] = page.colors || [];
      const newColors = current.filter(c => !colors.includes(c));

      if (newColors.length === current.length) continue;

      result.push({
        documentId: page.documentId,
        pageNum: page.pageNum,
        version: page.version,
        colors: newColors
      });
    }

    return result;
  }, []);

  // Update pages tag
  const updatePagesTag = useCallback((pages:any[], tag:string | null) => {
    const result = [];
    for ( let page of pages ){
      result.push({
        documentId: page.documentId,
        pageNum: page.pageNum,
        version: page.version,
        tag
      });
    }
    return result;
  }, []);

  // Page rotation
  const getManualRotationValue = useCallback((annotations?:any[]) => {
    if ( !annotations || !annotations.length ) return 0;
    const manualRotation = annotations.find((annotation:any) => annotation.name === 'manualRotation');
    if ( !manualRotation ) return 0;
    return Number(manualRotation.value);
  }, []);

  const updatePagesRotation = useCallback((pages:any[], degrees:number) => {
    const result = [];
    for ( let i = 0; i < pages.length; i++ ){
      const page = pages[i];
      // Get manual rotation
      const manualRotation = getManualRotationValue(page.annotations);
      const rotation = (manualRotation + degrees) % 360;

      result.push({
        documentId: page.documentId,
        pageNum: page.pageNum,
        version: page.version,
        rotation
      });
    }

    return result;
  }, [getManualRotationValue]);

  return {
    getPagesByIds,
    updatePagesInWorkspace,
    assignPagesColors,
    unassignPagesColors,
    updatePagesTag,
    updatePagesRotation
  };
};

export default usePagesUtilities;
